import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader, Sparkles, Edit3, FileText, Image as ImageIcon, Save, Send, X } from 'lucide-react';
import MediaPickerModal from '@/components/MediaPickerModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SimpleBlogGenerator() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showUnsplash, setShowUnsplash] = useState(false);
  
  // Input form
  const [rawContent, setRawContent] = useState('');
  const [audience, setAudience] = useState('general');
  const [length, setLength] = useState('medium');
  
  // Generated blog
  const [generatedBlog, setGeneratedBlog] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Finalization
  const [featuredImage, setFeaturedImage] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/test-categories`, {
        withCredentials: true
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const generateBlog = async () => {
    if (!rawContent.trim()) {
      toast.error('Please enter some content to generate from');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/simple-blog/generate`,
        {
          content: rawContent,
          audience: audience,
          length: length
        },
        { withCredentials: true }
      );

      setGeneratedBlog(response.data.blog);
      toast.success('Blog generated successfully!');
    } catch (error) {
      console.error('Blog generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate blog');
    } finally {
      setLoading(false);
    }
  };

  const refineBlog = async (action) => {
    if (!generatedBlog) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/simple-blog/refine`,
        {
          current_blog: generatedBlog,
          action: action  // 'simplify' or 'detail'
        },
        { withCredentials: true }
      );

      setGeneratedBlog(response.data.blog);
      toast.success(action === 'simplify' ? 'Blog simplified!' : 'More detail added!');
    } catch (error) {
      console.error('Blog refinement failed:', error);
      toast.error('Failed to refine blog');
    } finally {
      setLoading(false);
    }
  };

  const publishBlog = async (status) => {
    if (!generatedBlog) {
      toast.error('No blog to publish');
      return;
    }

    if (!selectedCategory) {
      toast.error('Please select a category');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/simple-blog/publish`,
        {
          blog: generatedBlog,
          featured_image: featuredImage,
          category: selectedCategory,
          status: status  // 'draft' or 'published'
        },
        { withCredentials: true }
      );

      toast.success(response.data.message);
      
      setTimeout(() => {
        navigate('/admin?tab=blogs');
      }, 2000);

    } catch (error) {
      console.error('Failed to publish blog:', error);
      toast.error('Failed to publish blog');
    } finally {
      setLoading(false);
    }
  };

  const updateBlogField = (field, value) => {
    setGeneratedBlog({ ...generatedBlog, [field]: value });
  };

  const updateSection = (index, field, value) => {
    const newSections = [...generatedBlog.sections];
    newSections[index][field] = value;
    setGeneratedBlog({ ...generatedBlog, sections: newSections });
  };

  return (
    <Layout>
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#2C1810] to-[#3D2817]">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="mb-8 text-center">
            <h1 className="text-5xl retro-title mb-4">Simple Blog Generator</h1>
            <p className="text-[#F4E4C1] text-lg">Transform raw content into engaging, accessible blog posts</p>
          </div>

          {!generatedBlog ? (
            /* Input Form */
            <div className="cassette-card p-8 mb-8">
              <h2 className="text-3xl retro-title text-[#2C1810] mb-6">Create Your Blog</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-[#2C1810] font-bold mb-2">Raw Content *</label>
                  <textarea
                    value={rawContent}
                    onChange={(e) => setRawContent(e.target.value)}
                    placeholder="Paste your technical notes, research, or raw content here..."
                    rows={12}
                    className="w-full px-4 py-3 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                  />
                  <p className="text-xs text-[#6B4423] mt-2">
                    Paste any content - technical notes, research, bullet points. AI will transform it!
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#2C1810] font-bold mb-2">Target Audience</label>
                    <select
                      value={audience}
                      onChange={(e) => setAudience(e.target.value)}
                      className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white"
                    >
                      <option value="patients">Simplify for Patients</option>
                      <option value="students">Educational for Students</option>
                      <option value="general">General Public</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[#2C1810] font-bold mb-2">Blog Length</label>
                    <select
                      value={length}
                      onChange={(e) => setLength(e.target.value)}
                      className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white"
                    >
                      <option value="short">Short (500-800 words)</option>
                      <option value="medium">Medium (800-1500 words)</option>
                      <option value="long">Long (1500+ words)</option>
                    </select>
                  </div>
                </div>

                <button
                  onClick={generateBlog}
                  disabled={loading}
                  className="retro-button w-full py-4 text-xl flex items-center justify-center gap-3"
                >
                  {loading ? (
                    <>
                      <Loader className="w-6 h-6 animate-spin" />
                      Generating Your Blog...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-6 h-6" />
                      Generate Blog
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            /* Blog Preview & Edit */
            <div className="space-y-6">
              
              {/* Refinement Buttons */}
              <div className="cassette-card p-4">
                <div className="flex items-center justify-between">
                  <div className="flex gap-3">
                    <button
                      onClick={() => refineBlog('simplify')}
                      disabled={loading}
                      className="retro-button py-2 px-4 flex items-center gap-2"
                    >
                      <Sparkles className="w-4 h-4" />
                      Simplify More
                    </button>
                    <button
                      onClick={() => refineBlog('detail')}
                      disabled={loading}
                      className="retro-button py-2 px-4 flex items-center gap-2"
                    >
                      <Edit3 className="w-4 h-4" />
                      Add More Detail
                    </button>
                  </div>
                  <button
                    onClick={() => {
                      setGeneratedBlog(null);
                      setRawContent('');
                      setFeaturedImage('');
                      setSelectedCategory('');
                    }}
                    className="text-red-500 hover:text-red-700 flex items-center gap-2"
                  >
                    <X className="w-5 h-5" />
                    Start Over
                  </button>
                </div>
              </div>

              {/* Blog Content */}
              <div className="cassette-card p-8">
                <div className="space-y-6">
                  
                  {/* Title */}
                  <div>
                    <label className="block text-[#2C1810] font-bold mb-2">Title</label>
                    <input
                      type="text"
                      value={generatedBlog.title}
                      onChange={(e) => updateBlogField('title', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-[#6B4423] rounded text-2xl font-bold"
                    />
                  </div>

                  {/* Introduction */}
                  <div>
                    <label className="block text-[#2C1810] font-bold mb-2">Introduction</label>
                    <textarea
                      value={generatedBlog.introduction}
                      onChange={(e) => updateBlogField('introduction', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 border-2 border-[#6B4423] rounded"
                    />
                  </div>

                  {/* Sections */}
                  {generatedBlog.sections?.map((section, index) => (
                    <div key={index} className="border-t-2 border-[#E8934D] pt-4">
                      <label className="block text-[#2C1810] font-bold mb-2">Section {index + 1} Heading</label>
                      <input
                        type="text"
                        value={section.heading}
                        onChange={(e) => updateSection(index, 'heading', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-[#6B4423] rounded font-bold mb-3"
                      />
                      <label className="block text-[#2C1810] font-bold mb-2">Content</label>
                      <textarea
                        value={section.content}
                        onChange={(e) => updateSection(index, 'content', e.target.value)}
                        rows={5}
                        className="w-full px-4 py-3 border-2 border-[#6B4423] rounded"
                      />
                    </div>
                  ))}

                  {/* Conclusion */}
                  <div className="border-t-2 border-[#E8934D] pt-4">
                    <label className="block text-[#2C1810] font-bold mb-2">Conclusion</label>
                    <textarea
                      value={generatedBlog.conclusion}
                      onChange={(e) => updateBlogField('conclusion', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 border-2 border-[#6B4423] rounded"
                    />
                  </div>

                  {/* Meta Description */}
                  <div>
                    <label className="block text-[#2C1810] font-bold mb-2">SEO Meta Description</label>
                    <textarea
                      value={generatedBlog.meta_description}
                      onChange={(e) => updateBlogField('meta_description', e.target.value)}
                      rows={2}
                      className="w-full px-4 py-3 border-2 border-[#6B4423] rounded"
                      maxLength={160}
                    />
                    <p className="text-xs text-[#6B4423] mt-1">
                      {generatedBlog.meta_description?.length || 0}/160 characters
                    </p>
                  </div>
                </div>
              </div>

              {/* Finalization */}
              <div className="admin-card p-6">
                <h3 className="text-2xl retro-title mb-4">Finalize & Publish</h3>
                
                <div className="space-y-4">
                  {/* Featured Image */}
                  <div>
                    <label className="block text-[#F4E4C1] font-bold mb-2">Featured Image (Optional)</label>
                    <button
                      type="button"
                      onClick={() => setShowUnsplash(true)}
                      className="retro-button mb-2"
                    >
                      <ImageIcon className="w-5 h-5 inline mr-2" />
                      Choose Image
                    </button>
                    {featuredImage && (
                      <div className="mt-2 relative">
                        <img src={featuredImage} alt="Featured" className="w-full h-48 object-cover rounded" />
                        <button
                          onClick={() => setFeaturedImage('')}
                          className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-[#F4E4C1] font-bold mb-2">Category *</label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                    >
                      <option value="">Select a category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Publish Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => publishBlog('draft')}
                      disabled={loading}
                      className="retro-button flex-1 py-3 flex items-center justify-center gap-2"
                    >
                      <Save className="w-5 h-5" />
                      Save as Draft
                    </button>
                    <button
                      onClick={() => publishBlog('published')}
                      disabled={loading}
                      className="retro-button flex-1 py-3 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700"
                    >
                      <Send className="w-5 h-5" />
                      Publish Now
                    </button>
                  </div>
                </div>
              </div>

            </div>
          )}

        </div>
      </div>

      {showUnsplash && (
        <MediaPickerModal
          isOpen={showUnsplash}
          onSelect={(url) => {
            setFeaturedImage(url);
            setShowUnsplash(false);
          }}
          onClose={() => setShowUnsplash(false)}
        />
      )}
    </Layout>
  );
}
